import { useState, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { RiskFilters, RisksTable, RiskStatsCards } from "../components";
import { risksData, getRiskStats } from "../data/risksData";
import type { RiskFilters as RiskFiltersType, Risk } from "../types";

type Order = "asc" | "desc";

const defaultFilters: RiskFiltersType = {
  search: "",
  types: [],
  severities: [],
  statuses: [],
  dateRange: { start: null, end: null },
};

export const RisksList = () => {
  const [filters, setFilters] = useState<RiskFiltersType>(defaultFilters);
  const [orderBy, setOrderBy] = useState<string>("timestamp");
  const [order, setOrder] = useState<Order>("desc");

  const stats = useMemo(() => getRiskStats(), []);

  const filteredRisks = useMemo(() => {
    let result = [...risksData];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (risk) =>
          risk.id.toLowerCase().includes(searchLower) ||
          risk.title.toLowerCase().includes(searchLower) ||
          risk.description.toLowerCase().includes(searchLower) ||
          risk.location.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filters.types.length > 0) {
      result = result.filter((risk) => filters.types.includes(risk.type));
    }

    // Apply severity filter
    if (filters.severities.length > 0) {
      result = result.filter((risk) => filters.severities.includes(risk.severity));
    }

    // Apply status filter
    if (filters.statuses.length > 0) {
      result = result.filter((risk) => filters.statuses.includes(risk.status));
    }

    return result;
  }, [filters]);

  const sortedRisks = useMemo(() => {
    const comparator = (a: Risk, b: Risk): number => {
      let aValue: string | number;
      let bValue: string | number;

      switch (orderBy) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "severity":
          const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          aValue = severityOrder[a.severity];
          bValue = severityOrder[b.severity];
          break;
        case "title":
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "status":
          const statusOrder = { active: 0, acknowledged: 1, investigating: 2, resolved: 3, closed: 4 };
          aValue = statusOrder[a.status];
          bValue = statusOrder[b.status];
          break;
        case "location":
          aValue = a.location.toLowerCase();
          bValue = b.location.toLowerCase();
          break;
        case "timestamp":
        default:
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    };

    return [...filteredRisks].sort(comparator);
  }, [filteredRisks, orderBy, order]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack spacing={2}>
        {/* Stats Cards */}
        <RiskStatsCards stats={stats} />

        {/* Filters */}
        <RiskFilters
          filters={filters}
          onFiltersChange={setFilters}
          totalCount={risksData.length}
          filteredCount={sortedRisks.length}
        />

        {/* Table */}
        <RisksTable
          risks={sortedRisks}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
        />
      </Stack>
    </Box>
  );
};
